import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Chip, Tooltip } from '@mui/material';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import { resources } from '../../../core/services/resources';
import type { ProjectType } from '../../../core/types/data-models';
import { recommendationDisplayFor } from '../../validation/recommendationDisplay';
import { formatScore } from '../../validation/scoring';

/**
 * The validation's verdict, as a chip in the project hero: the
 * recommendation (in the project type's own words) and the overall score,
 * linking to the summary step. Renders nothing until a summary is ready.
 */
export function ValidationVerdictChip({ projectId, projectType }: { projectId: number; projectType: ProjectType }) {
  const navigate = useNavigate();
  const summaryQuery = useQuery({
    queryKey: ['validation-summary', projectId],
    queryFn: () => resources.validationSummaryFor(projectId),
    enabled: Number.isFinite(projectId),
  });
  const summary = summaryQuery.data;
  if (!summary || summary.status !== 'READY' || !summary.recommendation || summary.overallScore == null) return null;

  const display = recommendationDisplayFor(projectType)[summary.recommendation];
  return (
    <Tooltip title="Open the validation summary">
      <Chip
        icon={<FactCheckOutlinedIcon />}
        label={`${display.label} · ${formatScore(summary.overallScore)}/10`}
        size="small"
        onClick={() => navigate(`/projects/${projectId}/validation?step=summary`)}
        sx={{
          fontWeight: 700,
          color: `var(--color-${display.color})`,
          bgcolor: `var(--color-${display.color}-soft)`,
          border: '1px solid',
          borderColor: `var(--color-${display.color}-line)`,
          '& .MuiChip-icon': { color: 'inherit' },
        }}
      />
    </Tooltip>
  );
}
